/**
 * <Money> — Helper para mostrar montos monetarios con:
 *   - Formato es-UY (miles `.`, decimal `,`).
 *   - Color según signo: +verde / −rojo / 0 neutro.
 *   - Alias de moneda role-aware (USDT→USD para gestor; USD→USD Clásica).
 *
 * Props:
 *   amount    : number          requerido
 *   currency  : string          requerido (UYU | USDT | USD | CUP | MLC)
 *   role      : string          opcional — defaults 'gestor'. Si 'admin' o 'payment_agent'
 *                               se muestran códigos técnicos (USDT, USD).
 *   sign      : 'auto'|'force'|'hide'  cómo mostrar +/-. default 'auto'.
 *   bold      : bool            negrita en el número
 *   compact   : bool            "1.5M" en vez de "1.500.000"
 *   testid    : string          data-testid
 */
import { displayCurrency, isAdminRole } from '@/utils/currencyDisplay';

const fmtEsUY = (n, compact) => {
  if (compact && Math.abs(n) >= 1000) {
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace('.', ',')}M`;
    if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(1).replace('.', ',')}k`;
  }
  return new Intl.NumberFormat('es-UY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(n);
};

export const Money = ({
  amount = 0,
  currency = 'UYU',
  role = 'gestor',
  sign = 'auto',
  bold = false,
  compact = false,
  className = '',
  testid,
}) => {
  const n = Number(amount) || 0;
  const isAdmin = isAdminRole(role);
  const code = displayCurrency(currency, { isAdmin });

  let colorCls;
  if (n > 0) colorCls = 'text-emerald-600';
  else if (n < 0) colorCls = 'text-rose-600';
  else colorCls = 'text-slate-500';

  let prefix = '';
  if (sign === 'force' && n >= 0) prefix = '+';
  else if (sign === 'auto' && n > 0) prefix = '+';

  const txt = `${prefix}${fmtEsUY(Math.abs(n) * (n < 0 ? -1 : 1), compact)}`;
  return (
    <span
      className={`font-mono ${bold ? 'font-bold' : ''} ${colorCls} ${className}`}
      data-testid={testid}
    >
      {txt} <span className="text-[0.85em] opacity-70">{code}</span>
    </span>
  );
};

export default Money;
